import axiosClient from "apis/axios-client";

export interface ReferralCreditConfigAPI {
  id: number;
  configName: string;
  referrerCreditAmount: number;
  refereeCreditAmount: number;
  nonCommunityReferrerCredit: number;
  nonCommunityRefereeCredit: number;
  communityCount: number;
  isActive: boolean;
  validTill: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ReferralCreditConfigRequest {
  configName: string;
  referrerCreditAmount: number;
  refereeCreditAmount: number;
  nonCommunityReferrerCredit: number;
  nonCommunityRefereeCredit: number;
  communityCount: number;
  isActive: boolean;
  validTill: string | null;
}

interface BaseApiResponse {
  message?: string;
}

/**
 * GET /api/ReferralCreditConfig/GetAll
 */
export const getAllReferralCreditConfig = async (): Promise<ReferralCreditConfigAPI[]> => {
  try {
    const response = (await axiosClient.get<ReferralCreditConfigAPI[]>(
      "/ReferralCreditConfig/GetAll"
    )) as unknown as ReferralCreditConfigAPI[];
    return response ?? [];
  } catch (error) {
    console.error("Error fetching referral credit configurations:", error);
    throw error;
  }
};

/**
 * GET /api/ReferralCreditConfig/GetById/{id}
 */
export const getReferralCreditConfigById = async (
  id: number
): Promise<ReferralCreditConfigAPI> => {
  try {
    const response = (await axiosClient.get<ReferralCreditConfigAPI>(
      `/ReferralCreditConfig/GetById/${id}`
    )) as unknown as ReferralCreditConfigAPI;
    return response;
  } catch (error) {
    console.error("Error fetching referral credit configuration:", error);
    throw error;
  }
};

/**
 * POST /api/ReferralCreditConfig/Create
 */
export const createReferralCreditConfig = async (
  payload: ReferralCreditConfigRequest
): Promise<BaseApiResponse> => {
  try {
    const sanitized: ReferralCreditConfigRequest = {
      ...payload,
      validTill: payload.validTill ? payload.validTill : null,
    };
    const response = (await axiosClient.post<BaseApiResponse>(
      "/ReferralCreditConfig/Create",
      sanitized
    )) as unknown as BaseApiResponse;
    return response ?? {};
  } catch (error: any) {
    console.error("[Referral][Create] failed", {
      status: error?.response?.status,
      data: error?.response?.data,
      url: error?.config?.url,
      payload,
    });
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      error.response?.data?.title ||
      (error.response?.data?.errors &&
        JSON.stringify(error.response.data.errors)) ||
      "Failed to create referral credit configuration";
    throw new Error(errorMessage);
  }
};

/**
 * PUT /api/ReferralCreditConfig/Update/{id}
 */
export const updateReferralCreditConfig = async (
  id: number,
  payload: ReferralCreditConfigRequest
): Promise<BaseApiResponse> => {
  try {
    const sanitized: ReferralCreditConfigRequest = {
      ...payload,
      validTill: payload.validTill ? payload.validTill : null,
    };
    const response = (await axiosClient.put<BaseApiResponse>(
      `/ReferralCreditConfig/Update/${id}`,
      sanitized
    )) as unknown as BaseApiResponse;
    return response ?? {};
  } catch (error: any) {
    console.error("[Referral][Update] failed", {
      status: error?.response?.status,
      data: error?.response?.data,
      url: error?.config?.url,
      id,
      payload,
    });
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      error.response?.data?.title ||
      (error.response?.data?.errors &&
        JSON.stringify(error.response.data.errors)) ||
      "Failed to update referral credit configuration";
    throw new Error(errorMessage);
  }
};

/**
 * DELETE /api/ReferralCreditConfig/Delete/{id}
 * NOTE: The delete curl was truncated — adjust the path if your endpoint differs.
 */
export const deleteReferralCreditConfig = async (id: number): Promise<void> => {
  try {
    await axiosClient.delete(`/ReferralCreditConfig/Delete/${id}`);
  } catch (error: any) {
    console.error("[Referral][Delete] failed", {
      status: error?.response?.status,
      data: error?.response?.data,
      url: error?.config?.url,
      id,
    });
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      error.response?.data?.title ||
      (error.response?.data?.errors &&
        JSON.stringify(error.response.data.errors)) ||
      "Failed to delete referral credit configuration";
    throw new Error(errorMessage);
  }
};
